import React, { useEffect, useState } from 'react';
import {
  View,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  useWindowDimensions,
  ScrollView,
  Linking,
} from 'react-native';
import Modal from 'modal-enhanced-react-native-web';
import { Header } from '../hello/header';
import FooterLanding from '../../components/footerComponentLanding/customFooter';
import MainDrawerMenu from '../hello/MainDrawer';
import getPlatformType from '../../helpers/helpers';
import Text from '../../components/text';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useSelector } from 'react-redux';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import { showErrorToast, showSuccesToast } from '../tenants/tenantOnboarding/helpers';
import TextInputComponent from '../../components/textinput/TextInputComponent';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../constants/theme';

const ContactUs = ({ navigation, isPublic }) => {
  const { width } = useWindowDimensions();
  const isMobile = width <= 1024;

  const userDetails = useSelector((state) => state?.Login?.userDetails);
  const userToken = useSelector((state) => state?.AppReducer?.token);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userDetails && Object.keys(userDetails).length > 0) {
      const fullName = [userDetails.first_name, userDetails.last_name].filter(Boolean).join(' ');
      if (fullName) setName(fullName);
      if (userDetails.email) setEmail(userDetails.email);
    } else if (userToken) {
      ajax
        .get(Urls.PROFILE)
        .then(({ data }) => {
          if (data?.status && data?.data?.userDetails) {
            const fetchedUser = data.data.userDetails;
            const fullName = [fetchedUser.first_name, fetchedUser.last_name].filter(Boolean).join(' ');
            if (fullName) setName(fullName);
            if (fetchedUser.email) setEmail(fetchedUser.email);
          }
        })
        .catch(console.log);
    }
  }, [userDetails, userToken]);

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = 'Name is required';
    if (!email.trim()) newErrors.email = 'Email address is required';
    if (!subject.trim()) newErrors.subject = 'Subject is required';
    if (!message.trim()) newErrors.message = 'Message text is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendMessage = () => {
    if (!validate()) return;

    setLoading(true);
    const payload = {
      name: name.trim(),
      email: email.trim(),
      subject: subject.trim(),
      message: message.trim(),
    };

    ajax
      .post(Urls.CONTACT_US, payload)
      .then(({ data }) => {
        setLoading(false);
        if (data?.data || data?.status) {
          showSuccesToast(data?.data || 'Your message has been sent successfully!');
          setSubject('');
          setMessage('');
        } else {
          showSuccesToast('Your message has been sent successfully!');
          setSubject('');
          setMessage('');
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log('error sending contact message', error);
        showErrorToast('Failed to send message. Please try again.');
      });
  };

  return (
    <ScrollView style={[styles.container, isMobile && styles.containerMobile]} showsVerticalScrollIndicator={false}>
      {/* Breadcrumb Header */}
      {!isPublic && (
        <Text style={styles.breadcrumbText}>Settings › Support › Contact Us</Text>
      )}

      <View style={styles.headerRow}>
        <Text style={styles.pageTitle}>Contact Us</Text>
      </View>

      {/* Main Content Grid: Left Form Card + Right Support Cards */}
      <View style={[styles.mainGridRow, isMobile && styles.mainGridRowMobile]}>
        {/* LEFT COLUMN: Send us a message form */}
        <View style={[styles.formCard, isMobile && styles.formCardMobile]}>
          <Text style={styles.cardTitle}>Send us a message</Text>

          {/* 2-Column Row for Name and Email */}
          <View style={[styles.inputGridRow, isMobile && styles.inputGridRowMobile]}>
            <View style={styles.inputCol}>
              <TextInputComponent
                title="Name *"
                placeholder="Your full name"
                value={name}
                onChangeText={(val) => {
                  setName(val);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
                }}
                error={errors.name}
                height={44}
              />
            </View>

            <View style={styles.inputCol}>
              <TextInputComponent
                title="Your Email Address *"
                placeholder="name@example.com"
                value={email}
                onChangeText={(val) => {
                  setEmail(val);
                  if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
                }}
                error={errors.email}
                height={44}
              />
            </View>
          </View>

          {/* Subject Field */}
          <View style={styles.fieldSpacing}>
            <TextInputComponent
              title="Subject of Message *"
              placeholder="e.g. Question regarding HubFlow deposit sync"
              value={subject}
              onChangeText={(val) => {
                setSubject(val);
                if (errors.subject) setErrors((prev) => ({ ...prev, subject: '' }));
              }}
              error={errors.subject}
              height={44}
            />
          </View>

          {/* Message TextArea Field */}
          <View style={styles.fieldSpacing}>
            <Text style={styles.fieldLabel}>Write your message here *</Text>
            <TextInput
              style={[styles.textAreaInput, errors.message && styles.inputErrorBorder]}
              placeholder="Type your detailed question, concern, or feedback here..."
              placeholderTextColor="#9CA3AF"
              multiline={true}
              numberOfLines={5}
              value={message}
              onChangeText={(val) => {
                setMessage(val);
                if (errors.message) setErrors((prev) => ({ ...prev, message: '' }));
              }}
            />
            {errors.message && <Text style={styles.errorText}>{errors.message}</Text>}
          </View>

          {/* Action Button */}
          <TouchableOpacity
            style={[styles.sendButton, loading && styles.sendButtonDisabled]}
            onPress={handleSendMessage}
            disabled={loading}
            activeOpacity={0.85}
          >
            <Text style={styles.sendButtonText}>{loading ? 'Sending...' : 'Send Message'}</Text>
          </TouchableOpacity>
        </View>

        {/* RIGHT COLUMN: Support Channels & Hours Cards */}
        <View style={[styles.infoColumn, isMobile && styles.infoColumnMobile]}>
          {/* Card 1: Direct Support Channels */}
          <View style={styles.supportChannelCard}>
            <Text style={styles.supportChannelTitle}>Direct Customer Support Channels</Text>
            <Text style={styles.supportChannelDesc}>
              For urgent document processing, system integration bugs, or legal inquiries, reach out via:
            </Text>

            <TouchableOpacity
              style={styles.emailRow}
              onPress={() => Linking.openURL('mailto:info@hubita.co.uk')}
              activeOpacity={0.8}
            >
              <View style={styles.mailIconBox}>
                <Feather name="mail" size={16} color="#6C2BD9" />
              </View>
              <Text style={styles.emailText}>info@hubita.co.uk</Text>
            </TouchableOpacity>
          </View>

          {/* Card 2: Operations & Support Hours */}
          <View style={styles.hoursCard}>
            <Text style={styles.hoursTitle}>Operations & Support Hours</Text>

            <View style={styles.hoursList}>
              <View style={styles.hourRow}>
                <Text style={styles.hourDay}>Monday – Friday</Text>
                <Text style={styles.hourTime}>9:00 AM – 5:30 PM</Text>
              </View>
              <View style={styles.hourRow}>
                <Text style={styles.hourDay}>Saturday</Text>
                <Text style={styles.hourTime}>10:00 AM – 2:00 PM</Text>
              </View>
              <View style={styles.hourRow}>
                <Text style={styles.hourDay}>Sunday</Text>
                <Text style={styles.hourTimeClosed}>Closed</Text>
              </View>
            </View>
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

const ContactUsWithDrawer = drawerHoc({
  Component: ContactUs,
  showBackButton: true,
  backNavigation: 'Setting',
});

const ContactUsPublicWrapper = (props) => {
  const userToken = useSelector((state) => state?.AppReducer?.token);
  const layoutType = getPlatformType();
  const isPhone = layoutType === 'phone';
  const [openDrawer, setDrawer] = useState(false);

  if (userToken) {
    return <ContactUsWithDrawer {...props} />;
  }

  return (
    <View style={{ flex: 1, backgroundColor: COLORS.appBg }}>
      <Header setDrawer={() => setDrawer(true)} />
      <ScrollView contentContainerStyle={{ flexGrow: 1 }} showsVerticalScrollIndicator={false}>
        <ContactUs {...props} isPublic={true} />
        <FooterLanding navigation={props.navigation} />
      </ScrollView>

      <Modal
        isVisible={openDrawer}
        onBackdropPress={() => setDrawer(false)}
        animationIn="slideInRight"
        animationOut="slideOutRight"
        style={{ margin: 0, width: '100%', right: 0 }}
      >
        <View style={{ width: isPhone ? '100%' : '50%', height: '100%', position: 'absolute', right: 0, top: 0 }}>
          <MainDrawerMenu setDrawer={setDrawer} />
        </View>
      </Modal>
    </View>
  );
};

export default ContactUsPublicWrapper;

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: COLORS.appBg,
  },
  containerMobile: {
    padding: 14,
  },

  breadcrumbText: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 6,
  },

  headerRow: {
    marginBottom: 20,
  },

  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },

  /* MAIN GRID */
  mainGridRow: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'flex-start',
    marginBottom: 40,
  },
  mainGridRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 16,
  },

  /* FORM CARD LEFT */
  formCard: {
    flex: 1.6,
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: 24,
    ...SHADOWS.sm,
  },
  formCardMobile: {
    flex: undefined,
    width: '100%',
  },

  cardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 18,
  },

  inputGridRow: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 14,
  },
  inputGridRowMobile: {
    flexDirection: 'column',
    gap: 14,
  },
  inputCol: {
    flex: 1,
  },

  fieldSpacing: {
    marginBottom: 14,
  },

  fieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
  },

  textAreaInput: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: BORDER_RADIUS.md,
    padding: 12,
    fontSize: 13,
    color: '#111827',
    minHeight: 120,
    textAlignVertical: 'top',
    outlineStyle: 'none',
  },
  inputErrorBorder: {
    borderColor: '#EF4444',
  },
  errorText: {
    fontSize: 11,
    color: '#EF4444',
    marginTop: 4,
  },

  sendButton: {
    backgroundColor: '#6C2BD9',
    borderRadius: BORDER_RADIUS.md,
    height: 44,
    paddingHorizontal: 28,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
    marginTop: 10,
  },
  sendButtonDisabled: {
    opacity: 0.6,
  },
  sendButtonText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#FFFFFF',
  },

  /* INFO COLUMN RIGHT */
  infoColumn: {
    flex: 1,
    gap: 16,
  },
  infoColumnMobile: {
    flex: undefined,
    width: '100%',
  },

  /* SUPPORT CHANNEL CARD */
  supportChannelCard: {
    backgroundColor: '#F5F3FF',
    borderWidth: 1,
    borderColor: '#E0E7FF',
    borderRadius: BORDER_RADIUS.lg,
    padding: 18,
  },
  supportChannelTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#6C2BD9',
    marginBottom: 6,
  },
  supportChannelDesc: {
    fontSize: 12,
    color: '#4B5563',
    lineHeight: 18,
    marginBottom: 14,
  },
  emailRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  mailIconBox: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  emailText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
  },

  /* HOURS CARD */
  hoursCard: {
    backgroundColor: COLORS.surface,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: BORDER_RADIUS.lg,
    padding: 18,
    ...SHADOWS.sm,
  },
  hoursTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 14,
  },
  hoursList: {
    gap: 10,
  },
  hourRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  hourDay: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
  hourTime: {
    fontSize: 12,
    fontWeight: '700',
    color: '#111827',
  },
  hourTimeClosed: {
    fontSize: 12,
    fontWeight: '600',
    color: '#9CA3AF',
  },
});
